<template>
  <WithLoader :isLoading="isLoading" classType="primary">
    <div
      class="letters-templates-table__wrapper"
      :class="{ [extraClass]: !!extraClass }"
    >
      <VDataTable
        :currentSort="{ ...sort, value: !!sort.order }"
        :headers="tableFields.headers"
        :rows="tableFields.rows"
        extraClass="letters-templates-table"
        @sort="onSort"
        classType="primary"
      >
      </VDataTable>
    </div>

    <TemplatesSendDripModal
      :dripLink="dripLink"
      :opened="sendDripModalOpened"
      @close="closeModal('sendDrip')"
    >
    </TemplatesSendDripModal>

    <TemplatesSendHubspotModal
      :hubspotLink="hubspotLink"
      :opened="sendHubspotModalOpened"
      :accountId="getAccount.id"
      :apiToken="getAccount.api_token"
      @close="closeModal('sendHubspot')"
    >
    </TemplatesSendHubspotModal>

    <VConfirmModal
      :isOpened="deleteTemplateConfirmOpened"
      @approve="_deleteTemplate"
      @cancel="deleteTemplateConfirmOpened = false"
      @close="deleteTemplateConfirmOpened = false"
    >
      <div class="confirm-message">
        Deleting the
        <span>
          {{ templateToDelete.name ? templateToDelete.name : "Noname" }} </span
        >template?
      </div>
    </VConfirmModal>
  </WithLoader>
</template>

<script>
import Vue from "vue";
import VueTypes from "vue-types";
import { mapActions, mapGetters } from "vuex";
import moment from "moment";

// components
import VDataTable from "@/components/VDataTable";
import TemplatesSendDripModal from "@/components/TemplatesSendDripModal";
import TemplatesSendHubspotModal from "@/components/TemplatesSendHubspotModal";
import VLinkMenu from "@/components/VLinkMenu";
import VPreviewImage from "@/components/VPreviewImage";
import VDateTooltiped from "@/components/VDateTooltiped";
import VConfirmModal from "@/components/VConfirmModal";
import VSwitcher from "@/components/VSwitcher";
import VEditableText from "@/components/VEditableText";

// mixins
import withNavigate from "@/mixins/withNavigate";
import withToasted from "@/mixins/withToasted";
import withCopy from "@/mixins/withCopy";

import { getFormattedDate } from "@/utils/util";

import { ActivityTrackingLabelsMap } from "@/constants/common";
import { getTableContextMenu } from "@/utils/table";

// utils
import { getLetterType, getIntegrationURL } from "@/utils/util";

//images
import postcardIcon from "@/assets/images/postcard-icon-light.svg";
import postcardIconActive from "@/assets/images/postcard-icon-active.svg";
import letterIcon from "@/assets/images/letter-icon.svg";
import letterIconActive from "@/assets/images/letter-icon-active.svg";

import { paginationFilterSortInitialObject } from "@/store/constants";

export default Vue.component("LettersTemplatesTable", {
  props: {
    items: VueTypes.arrayOf(
      VueTypes.shape({
        additional_service: VueTypes.oneOfType([VueTypes.string, VueTypes.any]),
        address_placement: VueTypes.oneOf([
          "top_first_page",
          "insert_blank_page"
        ]),
        color: VueTypes.bool,
        created_at: VueTypes.string,
        created_by_id: VueTypes.string,
        double_sided: VueTypes.bool,

        /*NEED TO FINISH, not full props list*/

        id: VueTypes.string,
        name: VueTypes.string,
        first_name: VueTypes.string,
        last_name: VueTypes.string,
        template_type: VueTypes.string,
        created: VueTypes.oneOfType([
          VueTypes.instanceOf(Date),
          VueTypes.string
        ]),
        edited: VueTypes.oneOfType([
          VueTypes.instanceOf(Date),
          VueTypes.string
        ]),
        type: VueTypes.string,
        size: VueTypes.string,
        preview: VueTypes.string,
        url_to_file: VueTypes.string
      })
    ).def([]),
    noMoreData: VueTypes.bool.def(false),
    hasNextPage: VueTypes.bool.def(false),
    extraClass: VueTypes.string.def("")
  },

  mixins: [withNavigate, withToasted, withCopy],

  components: {
    VDataTable,
    VConfirmModal,
    TemplatesSendHubspotModal,
    TemplatesSendDripModal
  },

  data() {
    return {
      deleteTemplateConfirmOpened: false,
      sendHubspotModalOpened: false,
      sendDripModalOpened: false,
      templateToDelete: {},
      dataKey: "letters",
      renameActiveId: null,
      templateId: null
    };
  },

  computed: {
    tableFields() {
      const { display_drip: drip, display_hubspot: hubspot } = this.getUser;

      return {
        headers: [
          {
            key: "name",
            label: "Template/Name",
            width: "30%",
            sortable: true,
            sortType: "string"
          },
          {
            key: "color",
            label: "Type",
            sortable: true,
            width: "20%"
          },
          {
            key: "created_at",
            label: "Created",
            sortable: true,
            width: "16%"
          },
          {
            key: "updated_at",
            label: "Modified",
            sortable: true,
            width: drip || hubspot ? "16%" : "27%"
          },
          {
            display: drip || hubspot,
            key: "v-link-menu",
            label: "Integrations",
            extraClass: "templates-actions",
            width: drip || hubspot ? "11%" : "0"
          },
          {
            key: "v-context-menu",
            label: "",
            component: VSwitcher,
            display: true,
            props: {
              type: "element",
              activeKey: this.dataKey,
              switcherKeys: {
                left: {
                  key: "postcards",
                  label: "",
                  icon: postcardIcon,
                  iconActive: postcardIconActive
                },

                right: {
                  key: "letters",
                  label: "",
                  icon: letterIcon,
                  iconActive: letterIconActive
                }
              },
              onClickHandler: this._updateLayout
            },
            width: "7%"
          }
        ],

        rows: this.items.map(template => ({
          key: template.id,
          click: () => {
            this.navigate({
              name: "letters-templates-settings",
              params: { id: template.id }
            });
          },
          values: [
            {
              key: "preview",
              component: VPreviewImage,
              props: {
                image: template.thumbnails_urls && template.thumbnails_urls[0],
                withPdfDownload: !!template.url_to_file,
                hideTitle: true,
                pdfDownloadHandler: e => this._downloadPdf(e, template),
                pdfLoading: this.downloadingPdfId === template.id,
                orientation: "vertical",

                // text component
                component: VEditableText,
                textComponentProps: {
                  _key: "name",
                  handleChange: (key, val) =>
                    this._updateLetterTemplate(template.id, key, val),
                  value: template.name,
                  hideLabel: true,
                  hideIcon: true,
                  extraClass: "letters-template-name",
                  activateOnClick: false,
                  inputActive: this.renameActiveId === template.id,
                  blurHandler: () => (this.renameActiveId = null)
                }
              },
              extraClass: "letters-templates-preview"
            },
            {
              key: "type",
              value: getLetterType(template.color, template.mail_type)
            },
            {
              key: "created_at",
              component: VDateTooltiped,
              props: {
                date: template.created_at
              }
            },
            {
              key: "updated_at",
              component: VDateTooltiped,
              props: {
                date: template.updated_at
              }
            },

            {
              display: drip || hubspot,
              key: "vlink-menu",
              component: VLinkMenu,
              props: {
                data: this.linkMenuForTemplates(template),
                dontHide: true,
                openOn: "hover"
              },
              extraClass: "drip-hub"
            },

            getTableContextMenu({
              rename: (e, a) => this._onRenameHandler(template.id),
              copy: (e, a) => this.copy(template.id),
              duplicate: (e, a) => this._onDuplicateHandler(template.id),
              remove: e =>
                this.handleDeleteTemplate(e, template.id, template.name)
            })
          ]
        }))
      };
    },

    dripLink() {
      return getIntegrationURL(this.templateId, "drip");
    },

    hubspotLink() {
      return getIntegrationURL(
        this.templateId,
        "hubspot",
        this.getAccount.api_token
      );
    },

    ...mapGetters("account", ["getAccount", "getUser"]),
    ...mapGetters("lettersTemplates", [
      "pagination",
      "sort",
      "search",
      "isLoading",
      "getTemplate"
    ])
  },

  methods: {
    async _downloadPdf(e, { url_to_file }) {
      e.stopPropagation();

      if (!url_to_file) {
        return;
      }

      window.open(url_to_file, "_blank");
    },

    closeModal(name) {
      if (name === "sendDrip") {
        this.sendDripModalOpened = false;
      } else if (name === "sendHubspot") {
        this.sendHubspotModalOpened = false;
      }

      this.templateId = null;
    },

    linkMenuForTemplates(template) {
      const { id, name } = template;
      const { display_drip: drip, display_hubspot: hubspot } = this.getUser;
      let items = [];

      if (drip) {
        items = [
          {
            name: "Send with Drip",
            onClick: e => this.sendWithDrip(e, id),
            key: "drip",
            extraClass: "drip"
          }
        ];
      }

      if (hubspot) {
        items = [
          ...items,
          {
            name: "Send with HubSpot",
            onClick: e => this.sendWithHubspot(e, id),
            key: "hubspot",
            extraClass: "hubspot"
          }
        ];
      }

      return items;
    },

    _updateLayout(active) {
      this.dataKey = active;
      localStorage.setItem(
        'mailpieceOption',
        this.dataKey
      );
      if (this.dataKey !== "letters") {
        this.navigate("/templates");
      }
    },

    _deleteTemplate() {
      this.deleteLetterTemplate({
        id: this.templateToDelete.id,
        onSuccess: () => {
          this.toast("Template has been deleted successfully.");
          this.deleteTemplateConfirmOpened = false;
        },

        onError: () => {
          this.deleteTemplateConfirmOpened = false;
          this.toast(
            "Failed to delete template. Please, try again later.",
            "error"
          );
        }
      });
    },

    _onDuplicateHandler(id) {
      const template = this.getTemplate(id);
      const {
        additional_service,
        address_placement,
        color,
        double_sided,
        embedding_data,
        file_url,
        name,
        perforated,
        type,
        html,
        thumbnails_urls,
        mail_type
      } = template;

      const objToCreate = {
        additional_service,
        address_placement,
        color,
        double_sided,
        embedding_data,
        file_url,
        html,
        perforated,
        type,
        name: `${name} - Copy`,
        thumbnails_urls,
        mail_type
      };

      //  todo: fix later
      if (!type) {
        objToCreate.type = "pdf";
      }

      this.createLetterTemplate({
        data: objToCreate,

        onSuccess: () => {
          this.fetchLettersTemplatesList({
            search: this.search,
            page_size: this.itemsPerPage
          });
          this.toast(`Template ${name} has been duplicated`);
        },
        onError: () => {
          this.toast("Failed to duplicate template", "error");
        }
      });
    },

    _onRenameHandler(id) {
      this.renameActiveId = id;
    },

    _updateLetterTemplate(id, key, val) {
      const self = this;
      if (!val) {
        this.toast(
          "Template name saving failed. Field can't be empty.",
          "error"
        );
      } else {
        this.updateLettersTemplate({
          data: { id: id, [key]: val },
          onSuccess: () => {
            this.toast("Template name has been changed!");
            self.renameActiveId = null;
          }
        });
      }
    },

    _onStatusClick(e, contactId, mailPieceId) {
      e.stopPropagation();
      this.$router.push(`/contacts/${contactId}?o=${mailPieceId}`);
    },

    _getFormattedDate(date) {
      return moment(date).format("MMM D, YYYY");
    },

    formatDate(date) {
      return getFormattedDate(date);
    },

    onSort(sort) {
      this.fetchLettersTemplatesList({
        sort,
        page: this.pagination.current_page,
        search: this.search,
        page_size: this.itemsPerPage
      });

      this.changeSort(sort);
    },

    handleDeleteTemplate(e, id, name) {
      e.stopPropagation();
      this.deleteTemplateConfirmOpened = true;
      this.templateToDelete = {
        id,
        name
      };
    },

    sendWithDrip(e, id) {
      e.stopPropagation();
      this.sendDripModalOpened = true;
      this.templateId = id;
    },

    sendWithHubspot(e, id) {
      e.stopPropagation();
      this.sendHubspotModalOpened = true;
      this.templateId = id;
    },

    ...mapActions("lettersTemplates", [
      "changeSort",
      "fetchLettersTemplatesList",
      "deleteLetterTemplate",
      "updateLettersTemplate",
      "createLetterTemplate"
    ])
  },

  destroyed() {
    this.changeSort(paginationFilterSortInitialObject.sort);
  }
});
</script>

<style lang="scss">
.letters-templates-table__wrapper /deep/ {
  table {
    table-layout: fixed;

    .v-editable-text__wrapper {
      &.letters-template-name {
        margin-bottom: 0;
      }
    }
  }
}

.drip-hub /deep/ li {
  cursor: pointer;
}
</style>
